import React, { useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import classNames from 'classnames';
import { validEmailRegex } from 'constants/regex';
import RestorePeopleIcon from 'icons/restore-people.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import useDebounce from 'components/hooks/useDebounce';

import Circle from 'components/application/Circle';
import { DropdownIndicator } from 'components/application/CollectionSelect';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import { personDisplayName } from 'components/helpers/users';

export default function SearchImportablePersonnelField({
  inclPersonnelWithProfileAccess,
  onInputChange,
  onMenuOpen,
  onSelection,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebounce(inputValue, 250);

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const queryParams = {
    search: inputValue,
    incl_personnel_with_profile_access: inclPersonnelWithProfileAccess,
  };

  const fetchImportablePersonnel = (page) =>
    axios.get('/users/importable_personnel', {
      params: {
        ...queryParams,
        page,
      },
    });

  const {
    data: domainImportablePersonnel,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ['importablePersonnel', queryParams],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await fetchImportablePersonnel(pageParam);
      return response.data;
    },
    getNextPageParam,
    enabled: isMenuOpen && inputValue === debouncedInputValue,
    select: (data) =>
      formatInfiniteData({
        data,
        resourceTypes: ['profilePhoto'],
      }),
  });

  const handleChange = (selected, _actionMeta) => {
    if (selected && selected.__isNew__) {
      onSelection({ isNotDerived: true, value: selected.value });
    } else {
      onSelection(selected);
    }
  };

  const handleInputChange = (val, { action }) => {
    if (action === 'input-change') {
      setInputValue(val);
      onInputChange(val);
    }
  };

  const selectOptions = (() => {
    if (!domainImportablePersonnel) return [];
    const emailUsageStatus =
      domainImportablePersonnel.meta.emailUsageStatus?.data.type;

    if (emailUsageStatus === 'restorableStatus') {
      return [
        {
          __isRestore__: true,
          value: null,
          label: null,
          restorableUser: {
            data: domainImportablePersonnel.meta.emailUsageStatus.included.find(
              (inclusion) => inclusion.type === 'user',
            ),
            included:
              domainImportablePersonnel.meta.emailUsageStatus.included.filter(
                (inclusion) => inclusion.type !== 'user',
              ),
          },
        },
      ];
    } else if (emailUsageStatus === 'inUseStatus') {
      return [
        {
          value: null,
          label: null,
          invalidReason: 'userExists',
        },
      ];
    }

    const personnelOptions = domainImportablePersonnel.data.map(
      (personnel) => ({
        personnel,
        profilePhoto:
          domainImportablePersonnel.included?.profilePhoto?.[
            personnel.relationships.profilePhoto.data?.id
          ],
      }),
    );

    const isCreatable =
      !domainImportablePersonnel.meta.exactPersonnelEmailMatch &&
      (personnelOptions.length === 0 || validEmailRegex.test(inputValue));

    return isCreatable ?
        [
          {
            __isNew__: true,
            value: inputValue,
            label: inputValue,
          },
          ...personnelOptions,
        ]
      : personnelOptions;
  })();

  return (
    <>
      <label className='collection-select__label tw-font-medium'>
        {i18nTShared('emailAddress')} *
      </label>
      <Select
        className='collection-select__select-container collection-select--transparent-option'
        classNamePrefix='collection-select'
        components={{
          DropdownIndicator,
          Option,
        }}
        filterOption={() => true}
        id='user_email'
        inputValue={inputValue}
        isLoading={isFetching || inputValue !== debouncedInputValue}
        isOptionDisabled={isOptionDisabled}
        loadingMessage={() => i18nTShared('loading...')}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onMenuOpen={() => {
          setIsMenuOpen(true);
          onMenuOpen();
        }}
        onMenuScrollToBottom={() => {
          if (hasNextPage) fetchNextPage();
        }}
        options={selectOptions}
        placeholder=''
        value={null}
      />
    </>
  );
}

SearchImportablePersonnelField.propTypes = {
  inclPersonnelWithProfileAccess: PropTypes.bool,
  onInputChange: PropTypes.func.isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onSelection: PropTypes.func.isRequired,
};

// helpers

function isOptionDisabled(option) {
  return (
    option.invalidReason ||
    (option.__isNew__ && !(option.value && validEmailRegex.test(option.value)))
  );
}

function Option(props) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.SearchImportablePersonnelField',
  });

  return (
    <components.Option {...props}>
      {props.data.__isNew__ ?
        <CreateOption isDisabled={props.isDisabled} value={props.data.value} />
      : props.data.__isRestore__ ?
        <RestoreOption restorableUser={props.data.restorableUser} />
      : props.data.invalidReason === 'userExists' ?
        <InvalidOption reason={i18nT(`errors.invalid.userExists`)} />
      : <PersonnelOption
          personnel={props.data.personnel}
          profilePhoto={props.data.profilePhoto}
        />
      }
    </components.Option>
  );
}

function CreateOption({ isDisabled, value }) {
  return (
    <span className='tw-inline-flex tw-items-center'>
      <Circle
        className={classNames(
          'tw-text-white',
          isDisabled ? 'tw-bg-blue-200' : 'tw-bg-blue-400',
        )}
      >
        <span className='-tw-mt-0.5 tw-ml-px tw-text-xl tw-font-semibold tw-tracking-tight'>
          +
        </span>
      </Circle>
      <span className='tw-ml-3'>
        <Trans
          components={{ span: <span className='tw-font-medium' /> }}
          i18nKey='settings.users.SearchImportablePersonnelField.createOption'
          ns='components'
          tOptions={{ interpolation: { escapeValue: false } }}
          values={{ value }}
        />
      </span>
    </span>
  );
}

function RestoreOption({ restorableUser }) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <>
      <div className='tw-mb-3 tw-mt-1 tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
        <p className='tw-m-0'>
          <Trans
            i18nKey='settings.users.SearchImportablePersonnelField.restoreOption'
            ns='components'
          />
        </p>
      </div>
      <div className='tw-mb-1 tw-flex tw-h-8'>
        <div className='tw-mr-3 tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-050'>
          <RestorePeopleIcon height={24} width={24} />
        </div>
        <div className='tw-leading-[32px]'>
          {i18nTShared('restoreName', {
            interpolation: { escapeValue: false },
            name: personDisplayName({
              attributes: restorableUser.data.attributes,
            }),
          })}
        </div>
      </div>
    </>
  );
}

function InvalidOption({ reason }) {
  return (
    <span className='tw-inline-flex tw-items-center'>
      <Circle className='tw-bg-red-025 tw-text-white'>
        <span className='tw-font-medium tw-text-red-800'>X</span>
      </Circle>
      <span className='tw-ml-3 tw-text-red-800'>{reason}</span>
    </span>
  );
}

function PersonnelOption({ personnel, profilePhoto }) {
  return (
    <div className='tw-flex tw-min-w-0 tw-items-center tw-gap-3'>
      <div className='tw-flex-none'>
        <ProfilePhotoDisplay
          componentSize='sm'
          photoUrl={profilePhoto?.links?.croppedUrl}
          profileInitials={`${personnel.attributes.firstName[0]}${personnel.attributes.lastName[0]}`}
        />
      </div>
      <div className='tw-min-w-0 tw-flex-auto'>
        <div className='tw-truncate tw-font-medium tw-text-grey-900'>
          {personnel.attributes.email}
        </div>
        <div className='tw-truncate tw-text-s tw-font-normal tw-tracking-wide'>
          {personDisplayName({ attributes: personnel.attributes })}
        </div>
      </div>
    </div>
  );
}
